import React, { useEffect, useState } from "react";
import { formatCurrency, formatPercent } from "../../util/NumberHelper";
import {
  ComponentRef,
  getComponentRef,
} from "../../message/components/records/MessageTagUtil";
import moment from "moment";
import { getElementToRender } from "./ElementDefinitions";
import { Box, Button, Card, Skeleton, Typography } from "@mui/material";
import { api } from "../../../App";
import { getMetricNameAndFormatter } from "../../util/Helpers";

const employeeSalesDataUrl = "/api/log-book/employee/sales-data";

const SalesLabels = {
  netSales: { label: "Net Sales", format: formatCurrency },
  comps: { label: "Comps", format: formatCurrency },
  voids: { label: "Voids", format: formatCurrency },
  ccTips: { label: "CC Tips", format: formatCurrency },
  ccTipsPer: { label: "CC Tips %", format: formatPercent },
  checkAvg: { label: "Check Avg", format: formatCurrency },
  guestsAvg: { label: "Guests Avg", format: formatCurrency },
};

export default function EmployeeSalesData(props) {
  const {
    label,
    id,
    readOnly,
    required,
    onChange,
    //onSaveDraft = () => {},
    formulaType,
    value,
    formValues,
    formScores,
    selectedSalesData,
    selectedSalesContests,
  } = props;
  const {
    handleSubComponentLoaded = (
      subComponent = { ...ComponentRef },
      isLoaded = false
    ) => {},
  } = props;
  const { itemIndex, level, messageTime } = props;

  const [internalValue, setInternalValue] = useState(value);
  const [isSalesDataLoading, setIsSalesDataLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [salesContests, setSalesContests] = useState([]);

  const loadSalesData = async () => {
    setIsSalesDataLoading(true);
    try {
      const { startDate, endDate, employeeId } = internalValue;
      const response = await api.getByUrl(
        `${employeeSalesDataUrl}/${employeeId}/${startDate}/${endDate}`
      );
      handleSubComponentLoaded(
        getComponentRef("EmployeeSalesData", itemIndex, level, messageTime),
        true
      );

      //console.log("response", response.data.result);
      setSalesData(response.data.result);
      handleOnChange("salesData", response.data.result);
    } catch (error) {
      console.error("Error loading sales data:", error);
    } finally {
      setIsSalesDataLoading(false);
    }
  };

  const loadSalesContests = async () => {
    //setIsDataLoading(true);
    try {
      const payload = {
        startDate: internalValue.startDate,
        endDate: internalValue.endDate,
        employeeId: internalValue.employeeId,
      };
      const response = await api.getSalesContestDashboardView(payload);
      //console.log("response", response.data.result);

      let result = response.data.result?.filter(
        (item) => !item.reportingOnly && item.recurring
      );
      const shouldBeFiltered =
        selectedSalesContests && selectedSalesContests.length > 0;
      if (shouldBeFiltered) {
        result = result.filter((item) =>
          selectedSalesContests.includes(item.recurringInitialParentId)
        );
      }
      setSalesContests(result);
    } catch (error) {
      console.error("Error loading sales contests data:", error);
    } finally {
      //setIsDataLoading(false);
    }
  };

  // Generic way to handle the onChange event for all fields.
  const handleOnChange = (key, value) => {
    if (key !== "salesContests") {
      let updatedValue;
      if (key === "employee") {
        updatedValue = {
          ...internalValue,
          employeeId: value.value,
          employeeName: value.label,
        };
      } else {
        updatedValue = { ...internalValue, [key]: value };
      }
      setInternalValue(updatedValue);
      onChange(id, updatedValue);
    } else if (key === "salesContests") {
      setInternalValue((prev) => {
        const _salesContest = prev?.salesContests || [];
        const filteredSalesContests = _salesContest.filter(
          (contest) => contest.id !== value.id
        );
        const updatedSalesContests = [...filteredSalesContests, value];
        const updatedValue = { ...prev, salesContests: updatedSalesContests };
        onChange(id, updatedValue);
        return updatedValue;
      });
    }
  };

  useEffect(() => {
    if (internalValue) {
      setInternalValue(value);
    }
  }, [value]);

  useEffect(() => {
    handleSubComponentLoaded(
      getComponentRef("EmployeeSalesData", itemIndex, level, messageTime),
      true
    );

    if (!readOnly) {
      const today = moment(new Date()).format("YYYY-MM-DD");
      const updatedValue = {
        ...internalValue,
        startDate: today,
        endDate: today,
      };

      setInternalValue(updatedValue);
      onChange(id, updatedValue);
    }
  }, []);

  const renderEmployeesSelect = () => {
    return getElementToRender(
      {
        type: 9,
        formElement: "SingleSelectInput",
        formElementDisplayName: "Employee Selector",
        label: "Select Employee",
        placeholderText: "Choose an employee...",
        icon: "fa fa-user",
        required: true,
        canEditSettings: false,
        loadOptionsFromUrl: true,
        optionsUrl: "/api/log-book/employees",
        options: [],
        dataType: "string",
        value: internalValue?.employeeId
          ? {
              value: internalValue?.employeeId,
              label: internalValue?.employeeName,
            }
          : undefined,
        disabled: !!value?.employeeId,
      },
      null,
      (id, newValue) => {
        setInternalValue((prevState) => ({
          ...prevState,
          employeeId: newValue.value,
          employeeName: newValue.label,
        }));
        handleOnChange("employee", newValue);
      }
    );
  };

  const renderStartDateInput = () => {
    return getElementToRender(
      {
        type: 1,
        formElement: "DateInput",
        formElementDisplayName: "Start Date",
        label: "Start Date",
        icon: "fa fa-calendar-o",
        required: true,
        canEditSettings: true,
        dataType: "string",
      },
      null,
      (id, newValue) => {
        const date = moment(newValue).format("YYYY-MM-DD");
        setInternalValue((prevState) => ({ ...prevState, startDate: date }));
        handleOnChange("startDate", date);
      }
    );
  };

  const renderEndDateInput = () => {
    return getElementToRender(
      {
        type: 1,
        formElement: "DateInput",
        formElementDisplayName: "End Date",
        label: "End Date",
        icon: "fa fa-calendar-o",
        required: true,
        canEditSettings: true,
        dataType: "string",
        value: internalValue?.endDate,
      },
      null,
      (id, newValue) => {
        const date = moment(newValue).format("YYYY-MM-DD");
        setInternalValue((prevState) => ({ ...prevState, endDate: date }));
        handleOnChange("endDate", date);
      }
    );
  };

  const renderDataRow = (key, value) => {
    if (value === undefined) {
      return null;
    }

    //console.log('key', key, 'label', SalesLabels[key].label, 'value', value);
    const format = SalesLabels[key]?.format || ((val) => val);
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1,
        }}
      >
        <Box sx={{ flex: 1.2 }}>
          <Typography variant="body1" fontWeight="bold" color="text.primary">
            {SalesLabels[key]?.label}
          </Typography>
        </Box>
        <Box sx={{ flex: 0.8, textAlign: "right" }}>
          <Typography variant="body1">{format(value)}</Typography>
        </Box>
      </Box>
    );
  };

  const renderSalesData = () => {
    if (!isSalesDataLoading && !salesData && !internalValue?.salesData) {
      return null;
    }

    const employeeSalesData = readOnly
      ? internalValue?.salesData
      : salesData ?? [];
    const salesDataKeys = selectedSalesData ?? Object.keys(SalesLabels);
    return (
      <>
        <Box sx={{ margin: 2 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="primary"
            gutterBottom
          >
            Sales Data
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: 1,
              borderColor: "divider",
              pb: 1,
              mb: 1,
            }}
          >
            <Box sx={{ flex: 1.2 }}>
              <Typography
                variant="body1"
                fontWeight="bold"
                color="text.secondary"
              >
                Name
              </Typography>
            </Box>
            <Box sx={{ flex: 0.8, textAlign: "right" }}>
              <Typography
                variant="body1"
                fontWeight="bold"
                color="text.secondary"
              >
                Total
              </Typography>
            </Box>
          </Box>
          {!isSalesDataLoading ? (
            salesDataKeys.map((key) => (
              <React.Fragment key={key}>
                {renderDataRow(key, employeeSalesData[key])}
              </React.Fragment>
            ))
          ) : (
            <Box>
              {[1, 2].map((t) => {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      my: 2,
                    }}
                    key={t}
                  >
                    <Box>
                      <Skeleton variant="rounded" width={150} height={35} />
                    </Box>
                    <Box>
                      <Skeleton variant="rounded" width={75} height={35} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </>
    );
  };

  const renderSalesContests = () => {
    if (
      (!salesContests || salesContests.length === 0) &&
      (!internalValue?.salesContests ||
        internalValue?.salesContests.length === 0)
    ) {
      return null;
    }

    const contests = readOnly
      ? internalValue?.salesContests
      : salesContests ?? [];
    return (
      <>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          color="primary"
          gutterBottom
        >
          Sales Contests
        </Typography>
        <Box sx={{ margin: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "row", mb: 1 }}>
            <Box sx={{ flex: 1.5 }}>
              <Typography
                variant="body1"
                fontWeight="bold"
                color="text.secondary"
              >
                Name
              </Typography>
            </Box>
            <Box sx={{ flex: 0.8 }}>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ textAlign: "center" }}
                color="text.secondary"
              >
                Rank
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ textAlign: "right" }}
                color="text.secondary"
              >
                Total
              </Typography>
            </Box>
          </Box>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }} />
          {contests.map((contest) => (
            <Box key={contest.id} sx={{ mb: 1 }}>
              <EmployeeSalesContest
                salesContestId={contest.id}
                salesContestInfo={contest}
                employeeId={internalValue.employeeId}
                onChange={handleOnChange}
                readOnly={readOnly}
              />
            </Box>
          ))}
        </Box>
      </>
    );
  };

  const renderReadOnly = () => {
    if (
      !internalValue?.employeeId ||
      !internalValue?.startDate ||
      !internalValue?.endDate
    ) {
      return false;
    }

    return (
      <Card sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 4, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="subtitle1" color="text.secondary">
              Start Date
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              {internalValue?.startDate}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="subtitle1" color="text.secondary">
              End Date
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              {internalValue?.endDate}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            Employee
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            {internalValue?.employeeName}
          </Typography>
        </Box>
        {renderSalesData()}
        {renderSalesContests()}
      </Card>
    );
  };

  const render = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {label}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            {renderStartDateInput()}
            {renderEndDateInput()}
          </Box>
          {renderEmployeesSelect()}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const { startDate, endDate, employeeId } = internalValue;
                if (startDate && endDate && employeeId) {
                  loadSalesData();
                  loadSalesContests();
                }
              }}
              disabled={
                !internalValue?.employeeId ||
                !internalValue?.startDate ||
                !internalValue?.endDate
              }
            >
              Apply
            </Button>
          </Box>
        </Box>
        <Box sx={{ flex: 1, mt: 2 }}>{renderSalesData()}</Box>
        <Box sx={{ mt: 2 }}>{renderSalesContests()}</Box>
      </Card>
    );
  };

  return readOnly ? renderReadOnly() : render();
}

const EmployeeSalesContest = ({
  salesContestId,
  salesContestInfo,
  employeeId,
  onChange,
  readOnly,
}) => {
  const [salesContestData, setSalesContestData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadSalesContestData = async () => {
    try {
      setIsLoading(true);
      const response = await api.getSalesContestDashboardViewById(
        employeeId,
        salesContestId
      );
      //console.log("response", response.data.result);
      setSalesContestData(response.data.result);
    } catch (error) {
      console.error("Error loading sales contest data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSalesContestData();
  }, []);

  if (!salesContestData && !salesContestInfo) {
    return null;
  }

  const contestData = readOnly ? salesContestInfo : salesContestData;
  const { contestTotalFormatter } = getMetricNameAndFormatter(
    contestData?.contestType
  );
  return (
    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
      <Box sx={{ flex: 1.5 }}>
        <Typography variant="body1" fontWeight="bold" color="text.secondary">
          {salesContestInfo?.name}
        </Typography>
      </Box>
      <Box sx={{ flex: 0.8 }}>
        {contestData?.rank !== undefined ? (
          <Typography
            variant="body1"
            fontWeight="bold"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            {contestData?.rank}
          </Typography>
        ) : (
          <Box sx={{ width: 35, height: 25 }}>
            <Skeleton variant="rounded" width={35} height={25} />
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1 }}>
        {contestData?.total !== undefined ? (
          <Typography
            variant="body1"
            fontWeight="bold"
            color="text.secondary"
            sx={{ textAlign: "right" }}
          >
            {contestTotalFormatter(contestData?.total)}
          </Typography>
        ) : (
          <Box sx={{ width: 45, height: 25 }}>
            <Skeleton variant="rounded" width={45} height={25} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
